import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import complaintTypeConfigApi from '../../api/complaintTypeConfig';
import moment from 'moment';

// 格式化时间数组为字符串
const formatTimeArray = (timeArray) => {
  if (Array.isArray(timeArray) && timeArray.length >= 6) {
    return moment(new Date(timeArray[0], timeArray[1] - 1, timeArray[2], 
                           timeArray[3], timeArray[4], timeArray[5])).format('YYYY-MM-DD HH:mm:ss');
  }
  return '-';
};

// 分页查询投诉类型配置列表
export const fetchComplaintTypeConfigList = createAsyncThunk(
  'complaintTypeConfig/fetchList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1, 
        pageSize = 10, 
        sortField = 'create_time', 
        sortOrder = 'desc',
        name,
        status,
        defaultPriority,
        ...otherParams
      } = params;

      const queryParams = {
        page,
        pageSize,
        sortField,
        sortOrder
      };

      // 添加搜索参数
      if (name) queryParams.name = name;
      if (status !== undefined) queryParams.status = status;
      if (defaultPriority) queryParams.defaultPriority = defaultPriority;

      const response = await complaintTypeConfigApi.listComplaintTypeConfigs(queryParams);
      
      // 处理响应数据
      const processedData = {
        records: (response.data?.records || []).map(item => ({
          ...item,
          createTime: formatTimeArray(item.createTime),
          updateTime: formatTimeArray(item.updateTime),
          
          // 大类映射
          categoryText: {
            1: '投诉',
            2: '建议',
            3: '反馈'
          }[item.category] || '未知',

          // 优先级映射
          priorityText: {
            1: '低',
            2: '中',
            3: '高',
            4: '紧急'
          }[item.defaultPriority] || '未知'
        })),
        total: response.data?.total || 0,
        current: response.data?.current || page,
        size: response.data?.size || pageSize,
        pages: response.data?.pages || 1
      };

      return processedData;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取投诉类型配置列表失败' });
    }
  }
);

// 创建投诉类型配置
export const createComplaintTypeConfig = createAsyncThunk(
  'complaintTypeConfig/create',
  async (configData, { rejectWithValue, dispatch }) => {
    try {
      const response = await complaintTypeConfigApi.createComplaintTypeConfig(configData);
      
      // 刷新列表
      dispatch(fetchComplaintTypeConfigList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '创建投诉类型配置失败' });
    }
  }
);

// 更新投诉类型配置
export const updateComplaintTypeConfig = createAsyncThunk(
  'complaintTypeConfig/update',
  async ({ id, configData }, { rejectWithValue, dispatch }) => {
    try {
      const response = await complaintTypeConfigApi.updateComplaintTypeConfig(id, configData);
      
      // 刷新列表
      dispatch(fetchComplaintTypeConfigList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '更新投诉类型配置失败' });
    }
  }
);

// 更新投诉类型配置状态
export const updateComplaintTypeConfigStatus = createAsyncThunk(
  'complaintTypeConfig/updateStatus',
  async ({ id, status }, { rejectWithValue, dispatch }) => {
    try {
      const response = await complaintTypeConfigApi.updateComplaintTypeConfigStatus(id, status);
      
      // 刷新列表
      dispatch(fetchComplaintTypeConfigList());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '更新投诉类型配置状态失败' });
    }
  }
);

const complaintTypeConfigSlice = createSlice({
  name: 'complaintTypeConfig',
  initialState: {
    complaintTypeConfigs: [],
    total: 0,
    current: 1,
    pageSize: 10,
    loading: false,
    error: null,
    currentConfig: null
  },
  reducers: {
    setCurrentConfig: (state, action) => {
      state.currentConfig = action.payload;
    }
  },
  extraReducers: (builder) => {
    // 获取投诉类型配置列表
    builder.addCase(fetchComplaintTypeConfigList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchComplaintTypeConfigList.fulfilled, (state, action) => {
      state.loading = false;
      state.complaintTypeConfigs = action.payload.records;
      state.total = action.payload.total;
      state.current = action.payload.current;
      state.pageSize = action.payload.size;
    });
    builder.addCase(fetchComplaintTypeConfigList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 创建投诉类型配置
    builder.addCase(createComplaintTypeConfig.fulfilled, (state) => {
      // 可以在这里添加额外的状态更新逻辑
    });

    // 更新投诉类型配置
    builder.addCase(updateComplaintTypeConfig.fulfilled, (state) => {
      // 可以在这里添加额外的状态更新逻辑
    });

    // 更新投诉类型配置状态
    builder.addCase(updateComplaintTypeConfigStatus.fulfilled, (state) => {
      // 可以在这里添加额外的状态更新逻辑
    });
  }
});

export const { setCurrentConfig } = complaintTypeConfigSlice.actions;
export default complaintTypeConfigSlice.reducer;
